import React, { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { calendarManagementService } from "../../services/api";
import { newRequestId } from "../../utils/requestId";
import { SeriesScheduleForm, SeriesScheduleReview } from "./SeriesSchedule";

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const field = "block text-sm font-semibold text-[#58635d]";
const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const responses = { needsAction: "Awaiting response", accepted: "Accepted", tentative: "Tentative", declined: "Declined" };
function localTime(value) {
  if (!value || value.length === 10) return value || "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}
function show(key, value, timezone) {
  if (key === "response_status") return responses[value] || "Unknown";
  if (Array.isArray(value)) return value.join(", ") || "No attendees";
  if ((key === "start" || key === "end") && value?.length > 10)
    return (
      new Date(value).toLocaleString(undefined, {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "short",
      }) + ` (${timezone})`
    );
  return value || "Empty";
}
function useRequest() {
  const previous = useRef(null);
  return [
    (payload) => {
      const serialized = JSON.stringify(payload);
      if (previous.current?.serialized !== serialized)
        previous.current = { serialized, id: newRequestId() };
      return { ...payload, request_id: previous.current.id };
    },
    () => {
      previous.current = null;
    },
  ];
}

function ChangeForm({ event: initialEvent, busy, save, close }) {
  const [event] = useState(initialEvent);
  const [values, setValues] = useState(event.values);
  const [allDay, setAllDay] = useState(event.values.start.length === 10);
  const [start, setStart] = useState(localTime(event.values.start));
  const [end, setEnd] = useState(localTime(event.values.end));
  const [recipients, setRecipients] = useState(
    event.values.attendees.join(", "),
  );
  const [error, setError] = useState("");
  const [request] = useRequest();
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(e) => {
        e.preventDefault();
        setError("");
        let starts, ends;
        try {
          starts = allDay ? start : new Date(start).toISOString();
          ends = allDay ? end : new Date(end).toISOString();
          if (
            !allDay &&
            (localTime(starts) !== start || localTime(ends) !== end)
          )
            throw new Error();
          if (allDay === (event.values.start.length === 10)) {
            if (start === localTime(event.values.start))
              starts = event.values.start;
            if (end === localTime(event.values.end)) ends = event.values.end;
          }
        } catch {
          setError(
            "Choose valid local start and end times. This time may fall in a daylight-saving gap.",
          );
          return;
        }
        const next = {
          ...values,
          start: starts,
          end: ends,
          attendees: recipients
            .split(",")
            .map((value) => value.trim().toLowerCase())
            .filter(Boolean)
            .sort(),
        };
        const changes = Object.fromEntries(
          Object.entries(next).filter(([key, value]) => {
            if (key === "start" || key === "end")
              return allDay
                ? value !== event.values[key]
                : new Date(value).getTime() !==
                    new Date(event.values[key]).getTime() ||
                    event.values[key].length === 10;
            return JSON.stringify(value) !== JSON.stringify(event.values[key]);
          }),
        );
        save(request({ expected_revision: event.revision, changes }));
      }}
    >
      <h2 className="text-lg font-bold">Prepare meeting changes</h2>
      <p className="text-sm text-[#68746d]">
        Save a proposal first. Review its exact changes and attendee list before
        applying it to Google.
      </p>
      <label className={field}>
        Meeting title
        <input
          required
          maxLength={240}
          className={input}
          value={values.title}
          onChange={(e) => setValues({ ...values, title: e.target.value })}
        />
      </label>
      <label className={field}>
        Location
        <input
          maxLength={1000}
          className={input}
          value={values.location}
          onChange={(e) => setValues({ ...values, location: e.target.value })}
        />
      </label>
      <label className={field}>
        Description
        <textarea
          maxLength={20000}
          rows={3}
          className={input}
          value={values.description}
          onChange={(e) =>
            setValues({ ...values, description: e.target.value })
          }
        />
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={allDay}
          onChange={(e) => {
            setAllDay(e.target.checked);
            setStart(
              e.target.checked
                ? start.slice(0, 10)
                : start.slice(0, 10) + "T09:00",
            );
            setEnd(
              e.target.checked ? end.slice(0, 10) : end.slice(0, 10) + "T10:00",
            );
          }}
        />
        All-day event
      </label>
      <p className="text-xs text-[#68746d]">
        {allDay
          ? `Dates use the Google calendar timezone: ${event.calendar_timezone || event.timezone}. The end date is exclusive: a one-day meeting ends on the next date.`
          : `Enter times in your device timezone: ${deviceZone}. Review shows them in ${event.timezone}.`}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className={field}>
          Start
          <input
            required
            type={allDay ? "date" : "datetime-local"}
            className={input}
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </label>
        <label className={field}>
          {allDay ? "End date (exclusive)" : "End"}
          <input
            required
            type={allDay ? "date" : "datetime-local"}
            className={input}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </label>
      </div>
      <label className={field}>
        Attendee email addresses
        <textarea
          rows={2}
          className={input}
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
        />
      </label>
      <p className="text-xs text-[#68746d]">
        Comma-separated addresses. Removing an address removes that attendee.
        Applying the proposal requests Google notifications for affected
        attendees.
      </p>
      {error && <p role="alert">{error}</p>}
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          Save calendar proposal
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Close form
        </button>
      </div>
    </form>
  );
}

function ResponseForm({ event: initialEvent, busy, save, close }) {
  const [event] = useState(initialEvent);
  const [response, setResponse] = useState("");
  const [request] = useRequest();
  return (
    <form className="surface-panel space-y-3 p-5" onSubmit={(e) => {
      e.preventDefault();
      save(request({ expected_revision: event.revision, response_status: response }));
    }}>
      <h2 className="font-bold">Prepare invitation response</h2>
      <p className="text-sm">Responding as {event.response.email} on {event.calendar_id}.</p>
      <label className={field}>Invitation response
        <select required className={input} value={response} onChange={(e) => setResponse(e.target.value)}>
          <option value="">Choose a response</option>
          {Object.entries(responses).filter(([value]) => value !== "needsAction").map(([value, label]) => (
            <option key={value} value={value} disabled={value === event.response.status}>{label}</option>
          ))}
        </select>
      </label>
      <p className="text-sm text-[#68746d]">Save a proposal, then review before sending to Google. Accepting or marking tentative does not check availability. Declining stops automatic meeting reminders; saved notes and agreed actions remain.</p>
      <div className="flex flex-wrap gap-2">
        <button disabled={busy || !response} className="primary-button">Save response proposal</button>
        <button type="button" className="secondary-button" onClick={close}>Close response form</button>
      </div>
    </form>
  );
}

function SeriesForm({ series: initialSeries, busy, save, close }) {
  const [series] = useState(initialSeries);
  const [values, setValues] = useState({ title: series.values.title, description: series.values.description, location: series.values.location });
  const [attendees, setAttendees] = useState(series.values.attendees.join(", "));
  const [request] = useRequest();
  return (
    <form className="surface-panel space-y-3 p-5" onSubmit={(e) => {
      e.preventDefault();
      const next = { ...values, attendees: attendees.split(",").map((value) => value.trim().toLowerCase()).filter(Boolean).sort() };
      const changes = Object.fromEntries(Object.entries(next).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(series.values[key])));
      save(request({ expected_revision: series.revision, changes }));
    }}>
      <h2 className="font-bold">Prepare whole-series changes</h2>
      <p className="text-sm">These details belong to the recurring series, including past and future meetings. Individually edited occurrences may retain exceptions in Google. Review before applying.</p>
      {[['title', 'Series title', 240], ['location', 'Series location', 1000], ['description', 'Series description', 20000]].map(([key, label, maxLength]) => (
        <label key={key} className={field}>{label}
          <textarea className={input} required={key === 'title'} maxLength={maxLength} rows={key === 'description' ? 3 : 1} value={values[key]} onChange={(e) => setValues({ ...values, [key]: e.target.value })} />
        </label>
      ))}
      <label className={field}>Series attendee email addresses
        <textarea className={input} rows={2} value={attendees} onChange={(e) => setAttendees(e.target.value)} />
      </label>
      <p className="text-sm">Comma-separated addresses. Clearing the list removes the series attendees. Google notifications are requested when you apply. Meeting dates, times and recurrence rules stay unchanged.</p>
      <div className="flex flex-wrap gap-2">
        <button className="primary-button" disabled={busy}>Save series proposal</button>
        <button type="button" className="secondary-button" onClick={close}>Close series form</button>
      </div>
    </form>
  );
}

function EndingReview({ change, timezone }) {
  const [page, setPage] = useState(0);
  const preview = change.before.preview;
  const rows = preview.affected;
  const zone = change.before.calendar_timezone || timezone;
  const stamp = value => show("start", value?.date || value?.dateTime, zone);
  return <section aria-label="Series ending preview" className="space-y-3 text-sm">
    <p className="font-semibold">End at the selected original occurrence. Earlier meetings, saved notes and unfinished commitments remain.</p>
    <p>First removed original occurrence: {stamp(change.before.boundary)}</p>
    <p>Moved meetings use their original series position. The ending applies to all following recurrence dates, including those beyond the preview. No replacement series is created.</p>
    <p>Review window: {show("start", preview.window_start, zone)} to {show("start", preview.window_end, zone)}. The list is limited to this window; previews expire after 30 minutes.</p>
    <p>{rows.length} affected Google occurrences · {preview.exceptions.length} exceptions in this window.</p>
    <table className="w-full text-left" aria-label="Occurrences affected by ending"><thead><tr><th className="p-2">Meeting</th><th className="p-2">Original start</th><th className="p-2">Current start / state</th></tr></thead><tbody>
      {rows.slice(page * 10, page * 10 + 10).map(row => <tr key={row.remote_id} className="border-t"><td className="p-2">{row.title}</td><td className="p-2">{stamp(row.original_start)}</td><td className="p-2">{row.status === "cancelled" ? "Already cancelled" : stamp(row.start)}</td></tr>)}
    </tbody></table>
    {rows.length > 10 && <div className="flex gap-2"><button className="secondary-button" disabled={!page} onClick={() => setPage(page - 1)}>Earlier affected meetings</button><button className="secondary-button" disabled={(page + 1) * 10 >= rows.length} onClick={() => setPage(page + 1)}>Later affected meetings</button></div>}
    <details><summary>Retained recurrence rules</summary><p className="break-words">{change.changes.trimmed_recurrence.join("; ")}</p></details>
    <p>Applying sends one series update and requests attendee notifications. Unconfirmed saved occurrences stay flagged for refresh; missing access is not treated as cancellation.</p>
    {change.result?.remaining_occurrences?.length > 0 && <p role="alert">Google still returns {change.result.remaining_occurrences.length} future occurrences in the review window. Inspect them in Google, then check the result again.</p>}
  </section>;
}

function Review({ change, eventId, timezone, busy, run }) {
  const following = change.operation === "split";
  const ending = change.operation === "end_following";
  const cancellation = change.operation === "cancel";
  const responding = change.operation === "respond";
  const wholeSeries = change.before.scope === "series";
  const rescheduling = change.operation === "reschedule" || following;
  const attendees = [
    ...new Set([
      ...change.before.values.attendees,
      ...(change.changes.attendees || change.changes.details?.attendees || []),
    ]),
  ];
  return (
    <article className="surface-panel space-y-3 p-5">
      <div className="flex justify-between gap-3">
        <h3 className="font-bold">
          {ending ? "End this and following meetings" : following ? "Change this and following meetings" : wholeSeries ? (cancellation ? "Cancel entire series" : "Update entire series") : responding ? "Respond to invitation" : cancellation ? "Cancel meeting" : "Update meeting"} ·{" "}
          {change.before.values.title}
        </h3>
        <span className="status-pill">
          {change.status.replaceAll("_", " ")}
        </span>
      </div>
      <p className="text-xs text-[#68746d]">
        {show("start", change.before.values.start, timezone)} →{" "}
        {show("end", change.before.values.end, timezone)} ·{" "}
        {change.before.calendar_id}
      </p>
      {following && <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
        <p className="font-semibold">Earlier occurrences are retained. This occurrence and following meetings move to a replacement series.</p>
        <p>Original boundary: {show("start", change.before.boundary.date || change.before.boundary.dateTime, timezone)}</p>
        <p>Google will receive two changes, with attendee notifications requested for each. New invitations request fresh responses. If interrupted, check the saved result before resuming.</p>
        <p className="mt-2 break-words">Retained series rules: {change.changes.trimmed_recurrence.join("; ")}</p>
      </div>}
      {wholeSeries && !following && !ending && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-semibold">Applies to the entire recurring series, including past and future meetings.</p>
          <p>{cancellation ? "All occurrences of this series will be cancelled. Saved notes and agreed actions remain." : rescheduling ? "This proposal changes the series schedule. Review projected dates, current exceptions and conflicts before applying." : "Individually edited occurrences may retain exceptions in Google. Dates, times and recurrence rules remain unchanged."}</p>
          <p className="mt-2 break-words">Recurrence: {change.before.schedule?.recurrence?.join("; ")}</p>
        </div>
      )}
      {following && <table className="w-full text-left text-sm" aria-label="Replacement meeting details">
        <thead><tr><th className="p-2">Field</th><th className="p-2">Before</th><th className="p-2">Replacement</th></tr></thead>
        <tbody>{Object.entries(change.changes.details).map(([key, value]) => <tr key={key} className="border-t">
          <th className="p-2 capitalize">{key}</th><td className="p-2 whitespace-pre-wrap break-words">{show(key, change.before.values[key], timezone)}</td><td className="p-2 whitespace-pre-wrap break-words">{show(key, value, timezone)}</td>
        </tr>)}</tbody>
      </table>}
      {!wholeSeries && change.before.recurring_instance && (
        <p className="text-sm">Applies to this occurrence only.</p>
      )}
      {String(change.event_id) !== String(eventId) && <Link className="text-sm underline" to={`/calendar/events/${change.event_id}`}>Open the occurrence where this proposal was prepared</Link>}
      {ending ? <EndingReview change={change} timezone={timezone} /> : rescheduling ? <SeriesScheduleReview change={change} /> : responding ? (
        <div className="space-y-2 text-sm">
          <p>Response for {change.before.response.email}: {responses[change.before.response.status] || "Unknown"} → <strong>{responses[change.changes.response_status]}</strong></p>
          <p>Google notifications will be requested for all guests. Delivery is not confirmed. Saved notes and agreed actions remain in Timelyzer.</p>
          <p>Accepting or marking tentative does not check availability.</p>
        </div>
      ) : cancellation ? (
        <p className="text-sm">
          Cancel this Google meeting and request notifications for its
          attendees. Saved notes and commitments stay in Timelyzer.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th className="p-2">Field</th>
                <th className="p-2">Before</th>
                <th className="p-2">After</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(change.changes).map(([key, value]) => (
                <tr key={key} className="border-t">
                  <th className="p-2 capitalize">{key}</th>
                  <td className="max-w-xs break-words whitespace-pre-wrap p-2">
                    {show(key, change.before.values[key], timezone)}
                  </td>
                  <td className="max-w-xs break-words whitespace-pre-wrap p-2">
                    {show(key, value, timezone)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!responding && <p className="text-sm">
        Affected attendees: {attendees.join(", ") || "No attendees"}
      </p>}
      {change.result?.message && (
        <p role="status" className="text-sm">
          {change.result.message}
        </p>
      )}
      {following && change.result?.detail && <p className="text-sm">{change.result.detail}</p>}
      {following && change.result?.replacement_event_id && <Link className="text-sm underline" to={`/calendar/events/${change.result.replacement_event_id}`}>Open the replacement series</Link>}
      {change.result?.pending_occurrences > 0 && <p className="text-sm">After this attempt, {change.result.pending_occurrences} saved occurrences still needed refresh. Use “Refresh series and saved occurrences” to check their current state without sending the change again.</p>}
      {change.result?.observed && (
        <details>
          <summary>Current Google values</summary>
          <dl className="mt-2 space-y-2 text-sm">
            {Object.entries(change.result.observed).map(([key, value]) => (
              <div key={key}>
                <dt className="font-semibold capitalize">{key}</dt>
                <dd className="whitespace-pre-wrap break-words">
                  {show(key, value, timezone)}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      )}
      {change.status === "draft" && (
        <div className="flex flex-wrap gap-2">
          <button
            disabled={busy || (rescheduling && change.before.preview.conflict_count > 0)}
            className="primary-button"
            onClick={() =>
              run(() => calendarManagementService.apply(change.id))
            }
          >
            {ending ? "End this and following Google meetings and notify attendees" : following ? "Apply to this and following meetings and notify attendees" : wholeSeries ? (cancellation ? "Cancel entire Google series and notify attendees" : "Apply to entire Google series and notify attendees") : responding ? "Send response to Google" : cancellation
              ? "Cancel Google meeting and notify attendees"
              : "Apply to Google and notify attendees"}
          </button>
          <button
            disabled={busy}
            className="secondary-button"
            onClick={() =>
              run(() => calendarManagementService.discard(change.id))
            }
          >
            Discard proposal
          </button>
        </div>
      )}
      {["needs_review", "applying"].includes(change.status) && (
        <button
          disabled={busy}
          className="secondary-button"
          onClick={() =>
            run(() => calendarManagementService.reconcile(change.id))
          }
        >
          Check Google result
        </button>
      )}
      {following && change.status === "needs_review" && change.result?.resumable && <button
        className="primary-button" disabled={busy} onClick={() => run(() => calendarManagementService.resume(change.id))}>
        Create the missing replacement and notify attendees
      </button>}
    </article>
  );
}

export function CalendarChangeWorkspace({ eventId }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [responding, setResponding] = useState(false);
  const [editingSeries, setEditingSeries] = useState(false);
  const [schedulingSeries, setSchedulingSeries] = useState(false);
  const [schedulingFollowing, setSchedulingFollowing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [before, setBefore] = useState(null);
  const [request, resetRequest] = useRequest();
  const {
    data,
    isLoading,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ["calendar-management", eventId, before],
    queryFn: () => calendarManagementService.get(eventId, before),
    refetchInterval: 15000,
  });
  const run = async (operation) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await operation();
      await Promise.all(
        [
          "calendar-management",
          "calendar-events",
          "event",
          "meeting",
          "today",
          "assistant",
          "contexts",
          "context",
        ].map((key) => client.invalidateQueries({ queryKey: [key] })),
      );
    } catch (failure) {
      const detail = failure.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => item.msg).join("; ")
            : "The calendar action could not complete. Your entered values are retained.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (isLoading) return <p role="status">Loading calendar review…</p>;
  if (loadError || !data)
    return (
      <div role="alert">
        <p>
          Calendar review unavailable.{" "}
          {typeof loadError?.response?.data?.detail === "string"
            ? loadError.response.data.detail
            : "Check the meeting link and try again."}
        </p>
        <button className="secondary-button" onClick={() => refetch()}>
          Retry calendar review
        </button>
        <Link to="/calendar">Back to Calendar</Link>
      </div>
    );
  const event = data.event;
  return (
    <div className="space-y-5">
      <Link className="text-sm font-semibold" to="/calendar">
        ← Calendar
      </Link>
      <header className="space-y-2">
        <p className="eyebrow">Connected calendar</p>
        <h1 className="page-title">{event.title}</h1>
        <p className="text-sm">
          {event.calendar_id} · {event.status}
        </p>
        <p className="text-xs text-[#68746d]">
          {event.observed_at
            ? `Last checked ${new Date(event.observed_at).toLocaleString()}`
            : "Refresh to retrieve current Google details."}
        </p>
      </header>
      {error && (
        <p role="alert" className="surface-panel p-4 text-red-800">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button
          disabled={busy}
          className="secondary-button"
          onClick={() => run(() => calendarManagementService.refresh(eventId))}
        >
          Refresh from Google
        </button>
        <Link className="secondary-button" to={`/meetings/${eventId}`}>
          Meeting notes and commitments
        </Link>
        {event.link && (
          <a
            className="secondary-button"
            href={event.link}
            target="_blank"
            rel="noreferrer"
          >
            Open Google Calendar
          </a>
        )}
      </div>
      {event.recurring_instance && (
        <p className="surface-panel p-4">
          This is one occurrence of a recurring meeting. Individual meeting controls affect
          this occurrence only. Use the separate whole-series controls to change the recurring series.
        </p>
      )}
      {event.series_id && (
        <section className="surface-panel space-y-3 p-5" aria-label="Recurring series">
          <h2 className="font-bold">Whole recurring series</h2>
          <p className="text-sm">Manage series details, attendees and scheduling, or cancel the whole series. Whole-series controls include past and future meetings.</p>
          <button className="secondary-button" disabled={busy} onClick={() => run(() => calendarManagementService.refreshSeries(eventId))}>
            {event.series ? "Refresh series and saved occurrences" : "Load series from Google"}
          </button>
          {data.series_pending_occurrences > 0 && <p role="status" className="text-sm">{data.series_pending_occurrences} saved occurrences still need refresh. Their displayed details may be out of date.</p>}
          {data.series_pending_records?.length > 0 && <details className="text-sm"><summary>Saved meeting records needing refresh</summary>
            <p>Google may have replaced these occurrence IDs. Their notes and actions are retained here; automatic reminders from unconfirmed times are paused.</p>
            <ul className="mt-2 space-y-2">{data.series_pending_records.map((row) => <li key={row.id}><Link className="underline" to={`/meetings/${row.id}`}>{row.title} · last known {show("start", row.last_known_start, event.timezone)} · notes and actions</Link></li>)}</ul>
          </details>}
          {event.series && <>
            <dl className="space-y-2 text-sm">
              <div><dt className="font-semibold">Series title</dt><dd>{event.series.values.title}</dd></div>
              <div><dt className="font-semibold">Series organizer</dt><dd>{event.series.organizer || "Not provided"}</dd></div>
              <div><dt className="font-semibold">Recurrence</dt><dd className="break-words">{event.series.recurrence.join("; ")}</dd></div>
              <div><dt className="font-semibold">First meeting</dt><dd>{show("start", event.series.values.start, event.timezone)} → {show("end", event.series.values.end, event.timezone)}</dd></div>
            </dl>
            <p className="text-xs text-[#68746d]">Series last checked {new Date(event.series.observed_at).toLocaleString()}</p>
            {event.series.reason ? <p className="text-sm">{event.series.reason}</p> : <div className="flex flex-wrap gap-2">
              <button className="primary-button" disabled={busy} onClick={() => setEditingSeries(true)}>Prepare whole-series changes</button>
              {event.series.schedule_form && <button className="secondary-button" disabled={busy} onClick={() => setSchedulingSeries(true)}>Preview a new series schedule</button>}
              <button className="secondary-button" disabled={busy} onClick={() => run(async () => {
                await calendarManagementService.prepareSeriesCancel(eventId, request({ expected_revision: event.series.revision }));
                resetRequest(); setBefore(null);
              })}>Prepare whole-series cancellation</button>
            </div>}
          </>}
        </section>
      )}
      {event.following && event.series && <section className="surface-panel space-y-3 p-5" aria-label="This and following meetings">
        <h2 className="font-bold">This and following meetings</h2>
        <p className="text-sm">Keep earlier meetings and change or end the series starting at this occurrence’s original time.</p>
        {event.following.reason ? <p className="text-sm">{event.following.reason}</p> : <div className="flex flex-wrap gap-2">
          <button className="primary-button" disabled={busy} onClick={() => setSchedulingFollowing(true)}>Prepare this and following changes</button>
          <button className="secondary-button" disabled={busy} onClick={() => run(async () => {
            await calendarManagementService.prepareFollowingCancel(eventId, request({ expected_revision: event.following.revision }));
            resetRequest(); setBefore(null);
          })}>Prepare this and following cancellation</button>
        </div>}
      </section>}
      {schedulingFollowing && event.following?.schedule_form && <SeriesScheduleForm following
        series={{ ...event.series, ...event.following }} busy={busy} close={() => setSchedulingFollowing(false)} save={payload => run(async () => {
          await calendarManagementService.prepareFollowing(eventId, payload);
          setSchedulingFollowing(false); setBefore(null);
        })} />}
      {editingSeries && event.series && <SeriesForm series={event.series} busy={busy} close={() => setEditingSeries(false)} save={(payload) => run(async () => {
        await calendarManagementService.prepareSeries(eventId, payload);
        setEditingSeries(false); setBefore(null);
      })} />}
      {schedulingSeries && event.series?.schedule_form && <SeriesScheduleForm series={event.series} busy={busy} close={() => setSchedulingSeries(false)} save={(payload) => run(async () => {
        await calendarManagementService.prepareSeriesSchedule(eventId, payload);
        setSchedulingSeries(false); setBefore(null);
      })} />}
      {event.values && (
        <section
          className="surface-panel space-y-3 p-5"
          aria-label="Current meeting details"
        >
          <h2 className="font-bold">Current meeting</h2>
          <dl className="space-y-2 text-sm">
            {Object.entries(event.values).map(([key, value]) => (
              <div key={key}>
                <dt className="font-semibold capitalize">
                  {key === "end" && value.length === 10
                    ? "End (exclusive)"
                    : key}
                </dt>
                <dd className="whitespace-pre-wrap break-words">
                  {show(key, value, event.timezone)}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-sm">
            Organizer: {event.organizer || "Not provided"}
          </p>
          {event.attendees?.length > 0 && (
            <details>
              <summary>Attendee responses</summary>
              <ul className="mt-2 text-sm">
                {event.attendees.map((person, index) => (
                  <li key={person.email || index}>
                    {person.email} · {person.responseStatus || "needsAction"}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}
      {event.response && (
        <section className="surface-panel space-y-3 p-5" aria-label="Invitation status">
          <h2 className="font-bold">Invitation response</h2>
          {event.response.email && <p className="text-sm">{event.response.email} · {responses[event.response.status] || "Unknown"}</p>}
          {event.response.status === "declined" && <p className="text-sm">Automatic meeting reminders are paused. Your notes and agreed actions are retained.</p>}
          {event.response.reason ? <p className="text-sm text-[#68746d]">{event.response.reason}</p> : (
            <button disabled={busy} className="primary-button" onClick={() => setResponding(true)}>Prepare invitation response</button>
          )}
        </section>
      )}
      {responding && event.response && (
        <ResponseForm key={eventId} event={event} busy={busy} close={() => setResponding(false)} save={(payload) => run(async () => {
          await calendarManagementService.prepareResponse(eventId, payload);
          setResponding(false);
          setBefore(null);
        })} />
      )}
      {event.reason ? (
        <p className="text-sm text-[#68746d]">{event.reason}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button
            disabled={busy}
            className="primary-button"
            onClick={() => setEditing(true)}
          >
            Prepare meeting changes
          </button>
          <button
            disabled={busy}
            className="secondary-button"
            onClick={() =>
              run(async () => {
                await calendarManagementService.prepareCancel(
                  eventId,
                  request({ expected_revision: event.revision }),
                );
                resetRequest();
              })
            }
          >
            Prepare cancellation
          </button>
        </div>
      )}
      {editing && (
        <ChangeForm
          event={event}
          busy={busy}
          close={() => setEditing(false)}
          save={(payload) =>
            run(async () => {
              await calendarManagementService.prepare(eventId, payload);
              setEditing(false);
            })
          }
        />
      )}
      <section className="space-y-3" aria-label="Reviewed calendar changes">
        <h2 className="text-lg font-bold">Reviewed calendar changes</h2>
        <p className="text-sm text-[#68746d]">
          Time changes check local events, the selected Google calendar and your
          primary calendar. Attendees’ availability is not checked. Google
          notifications are requested on apply; delivery to each guest is not
          confirmed.
        </p>
        {!data.changes.length && <p>No proposals yet.</p>}
        {data.changes.map((change) => (
          <Review
            key={change.id}
            change={change}
            eventId={eventId}
            timezone={event.timezone}
            busy={busy}
            run={run}
          />
        ))}
        <div className="flex gap-2">
          {before && (
            <button
              className="secondary-button"
              onClick={() => setBefore(null)}
            >
              Latest proposals
            </button>
          )}
          {data.next_before_id && (
            <button
              className="secondary-button"
              onClick={() => setBefore(data.next_before_id)}
            >
              Older proposals
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

export default function CalendarChangePage() {
  const { eventId } = useParams();
  return (
    <main className="app-page">
      <div className="app-page-inner">
        <CalendarChangeWorkspace key={eventId} eventId={eventId} />
      </div>
    </main>
  );
}
